import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';
import { CLEARED_CRM_SEARCH } from './useCrmViewParam';

export const OUTREACH_SUB_VIEWS = ['inbox', 'templates', 'crm'] as const;
export type OutreachSubView = (typeof OUTREACH_SUB_VIEWS)[number];

const isSubView = (value: string | undefined): value is OutreachSubView =>
  value !== undefined && (OUTREACH_SUB_VIEWS as readonly string[]).includes(value);

export function resolveOutreachSubView(tab: string | undefined, sub: string | undefined): OutreachSubView | null {
  if (tab !== 'outreach') return null;
  return isSubView(sub) ? sub : 'inbox';
}

/** True while the inbox shows one entry's thread (`?tab=outreach&entry=…`): the shell is then bounded so only the thread scrolls. */
export function isOutreachThreadOpen(search: Pick<Partial<CampaignSearch>, 'tab' | 'sub' | 'entry'>): boolean {
  return resolveOutreachSubView(search.tab, search.sub) === 'inbox' && !!search.entry;
}

export function useOutreachSubParam() {
  const { tab, sub, template, entry } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const navigate = useNavigate();
  const onOutreach = tab === 'outreach';
  const setSub = useCallback(
    (next: OutreachSubView) =>
      navigate({
        to: '.',
        search: (prev: Partial<CampaignSearch>) => ({
          ...prev,
          ...CLEARED_CRM_SEARCH,
          tab: 'outreach',
          sub: next,
          template: undefined,
          entry: undefined,
          pill: undefined,
          page: undefined,
        }),
      } as never),
    [navigate],
  );
  const setTemplate = useCallback(
    (next: string | number | null) => navigate({ to: '.', search: (prev: Partial<CampaignSearch>) => ({ ...prev, template: next ?? undefined }) } as never),
    [navigate],
  );
  const setEntry = useCallback(
    (next: string | null) => navigate({ to: '.', search: (prev: Partial<CampaignSearch>) => ({ ...prev, entry: next ?? undefined }) } as never),
    [navigate],
  );
  /** Opens one creator's thread in the inbox from anywhere in outreach (the CRM's creator lists). */
  const openThread = useCallback(
    (entryId: string) =>
      navigate({
        to: '.',
        search: (prev: Partial<CampaignSearch>) => ({
          ...prev,
          ...CLEARED_CRM_SEARCH,
          tab: 'outreach',
          sub: 'inbox',
          entry: entryId,
          template: undefined,
          pill: undefined,
          page: undefined,
        }),
      } as never),
    [navigate],
  );
  const openRosterChat = useCallback(
    (username: string) =>
      navigate({
        to: '.',
        search: (prev: Partial<CampaignSearch>) => ({
          ...prev,
          ...CLEARED_CRM_SEARCH,
          tab: 'creators',
          creator: username,
          panel: 'conversation',
          sub: undefined,
          entry: undefined,
          pill: undefined,
          page: undefined,
          template: undefined,
          outreach_sort: undefined,
          reached_from: undefined,
          reached_to: undefined,
          stage: undefined,
          intent: undefined,
          locked_rate: undefined,
        }),
      } as never),
    [navigate],
  );
  return {
    sub: resolveOutreachSubView(tab, sub),
    setSub,
    template: onOutreach && template != null ? String(template) : null,
    setTemplate,
    entry: onOutreach ? (entry ?? null) : null,
    setEntry,
    openThread,
    openRosterChat,
  };
}
